import React, { useState } from 'react';
import type { TextFieldProps } from '@mui/material';
import type { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import StyledTextField from './styledTextField';
import {
  formatNumericValue,
  numericInputValues,
  sanitizeNumericDraft,
  type NumericInputOptions,
} from '@/utils/numericInput';

export type NumericTextFieldProps = Omit<
  TextFieldProps,
  'onChange' | 'onBlur' | 'value' | 'defaultValue'
> &
  NumericInputOptions & {
    value?: string | number;
    onValueChange: (
      value: string | undefined,
      name?: string,
      values?: CurrencyInputOnChangeValues,
    ) => void;
    onBlur?: () => void;
  };

/**
 * Currency/percentage text field: it echoes what is typed while it has focus
 * and formats on blur. Formatting on every keystroke instead would rebuild the
 * text from the numeric value, and a typed group separator never survives that
 * round trip.
 */
const NumericTextField: React.FC<NumericTextFieldProps> = ({
  value,
  onValueChange,
  onBlur,
  allowDecimals,
  allowNegativeValue,
  decimalsLimit,
  intlConfig,
  prefix,
  name,
  slotProps,
  ...textFieldProps
}) => {
  const options: NumericInputOptions = {
    allowDecimals,
    allowNegativeValue,
    decimalsLimit,
    intlConfig,
    prefix,
  };

  const [draft, setDraft] = useState<string | null>(null);

  // No `value` prop means the caller keeps the number somewhere we cannot read
  // — Formik, in the interest-rate field — so the settled text has to stay here
  // or blur would blank a field whose value the form still holds.
  const uncontrolled = value == null;
  const incoming = uncontrolled ? '' : String(value);

  const report = (next: string) => {
    const { value: reported, values } = numericInputValues(next, options);
    onValueChange(reported, name, values);
    return values;
  };

  return (
    <StyledTextField
      {...textFieldProps}
      slotProps={{
        ...slotProps,
        // The decimal keypad the library used to ask for. It has to go on the
        // html input; MUI forwards an unknown prop to the root instead.
        htmlInput: {
          inputMode: 'decimal',
          ...(typeof slotProps?.htmlInput === 'object'
            ? slotProps.htmlInput
            : {}),
        },
      }}
      name={name}
      type='text'
      value={
        draft ?? (incoming === '' ? '' : formatNumericValue(incoming, options))
      }
      onChange={(event) => {
        const next = sanitizeNumericDraft(event.target.value, options);
        setDraft(next);
        report(next);
      }}
      onBlur={() => {
        if (draft !== null) {
          const { formatted } = report(draft);
          setDraft(uncontrolled ? formatted : null);
        }
        onBlur?.();
      }}
    />
  );
};

export default NumericTextField;
